import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import templatesService from "../../../../services/templates.service";
import { useEyeconxStore } from "../../../../store/eyeconxStore";
import { toast } from "react-toastify";
import SmsPhonePreview from "./SmsPhonePreview";

const SmsTemplates = () => {
  const clinicId = useEyeconxStore((state) => state.user.clinicId);

  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editing, setEditing] = useState(false);
  const [wordCount, setWordCount] = useState(0);
  const [characterCount, setCharacterCount] = useState(0);

  const { register, handleSubmit, reset, watch } = useForm();

  // Watch the content for live preview
  const contentValue = watch("content", "");

  useEffect(() => {
    loadTemplates();
  }, [clinicId]);

  // Update word and character count
  useEffect(() => {
    if (contentValue) {
      const words = contentValue.trim() ? contentValue.trim().split(/\s+/).length : 0;
      const characters = contentValue.length;
      setWordCount(words);
      setCharacterCount(characters);
    } else {
      setWordCount(0);
      setCharacterCount(0);
    }
  }, [contentValue]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const res = await templatesService.getSmsTemplates(clinicId);
      setTemplates(res.data || []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load SMS templates.");
    } finally {
      setLoading(false);
    }
  };

  const loadTemplateById = async (id) => {
    if (!id) return;
    setSelectedId(id);
    setEditing(false);

    try {
      setLoading(true);
      const res = await templatesService.getSmsTemplateById(clinicId, id);
      const data = res.data;

      setSelectedTemplate(data);
      reset({
        id: data.id,
        content: data.content,
        status: data.status,
        reminderTypeId: data.reminderTypeId,
      });

      // Initialize word/count for new template
      const words = data.content.trim() ? data.content.trim().split(/\s+/).length : 0;
      const characters = data.content.length;
      setWordCount(words);
      setCharacterCount(characters);

    } catch (err) {
      console.error(err);
      toast.error("Failed to load template.");
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true);

      await templatesService.updateSmsTemplate(clinicId, selectedId, {
        id: data.id,
        clinicId,
        content: data.content,
        status: data.status,
        reminderTypeId: data.reminderTypeId,
      });

      toast.success("Template updated successfully!");
      setEditing(false);
      loadTemplates();
      loadTemplateById(selectedId);

    } catch (err) {
      console.error(err);
      toast.error("Failed to update SMS template.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card border-0 shadow-sm">
      <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center">
        <h5 className="card-title mb-0 fw-semibold text-dark">
          <i className="bi bi-chat-left-text me-2 text-primary"></i>
          SMS Templates
        </h5>

        {selectedId && !editing && (
          <button
            className="btn btn-primary btn-sm d-flex align-items-center"
            onClick={() => setEditing(true)}
          >
            <i className="bi bi-pencil me-1"></i>
            Edit Template
          </button>
        )}

        {editing && (
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={() => {
              setEditing(false);
              loadTemplateById(selectedId);
            }}
          >
            Cancel
          </button>
        )}
      </div>

      <div className="card-body p-0">
        <div className="row g-0">

          {/* Sidebar: Template List */}
          <div className="col-md-4 border-end">
            <div className="p-3 border-bottom bg-light">
              <h6 className="mb-0 fw-semibold text-dark">Template Types</h6>
            </div>
            <div className="p-3" style={{ maxHeight: "70vh", overflowY: "auto" }}>
              {templates.map((template) => (
                <div
                  key={template.id}
                  onClick={() => loadTemplateById(template.id)}
                  className={`card mb-2 cursor-pointer ${
                    selectedId === template.id ? "border-primary" : ""
                  }`}
                  style={{
                    cursor: "pointer",
                    borderLeft: selectedId === template.id ? "4px solid #0d6efd" : "4px solid transparent",
                  }}
                >
                  <div className="card-body py-2">
                    <div className="d-flex justify-content-between align-items-start">
                      <h6 className={`card-title mb-1 ${selectedId === template.id ? "text-primary" : "text-dark"}`}>
                        {template.reminderName}
                      </h6>
                      {template.status && (
                        <span className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-25">
                          Active
                        </span>
                      )}
                    </div>
                    <small className="text-muted">
                      Updated: {new Date(template.modifiedAt).toLocaleDateString()}
                    </small>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Main Right Area */}
          <div className="col-md-8">
            {!selectedTemplate ? (
              <div className="text-center py-5 text-muted">
                <i className="bi bi-chat-left-text display-4 opacity-50"></i>
                <p className="mt-3 fs-5">Select a template to view or edit</p>
                <small>Choose from the list on the left</small>
              </div>
            ) : (
              <div className="p-4">
                <form onSubmit={handleSubmit(onSubmit)}>
                  <div className="row">

                    {/* LEFT COLUMN — Editor */}
                    <div className="col-md-7 border-end pe-4">

                      {/* Template Name */}
                      <div className="mb-4">
                        <label className="form-label fw-semibold text-dark">
                          Template Name
                        </label>
                        <div className="bg-light p-3 rounded border">
                          <h6 className="mb-0">{selectedTemplate.reminderName}</h6>
                        </div>
                      </div>

                      {/* Status Switch */}
                      <div className="mb-4">
                        <label className="form-label fw-semibold text-dark">
                          Status
                        </label>
                        <div className="bg-light p-3 rounded border">
                          <div className="form-check form-switch mb-0">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              disabled={!editing}
                              {...register("status")}
                            />
                            <label className="form-check-label fw-semibold">
                              {selectedTemplate.status ? "Active" : "Inactive"}
                            </label>
                          </div>
                        </div>
                      </div>

                      {/* SMS Content Editor */}
                      <div className="mb-4">
                        <div className="d-flex justify-content-between mb-2">
                          <label className="form-label fw-semibold text-dark">
                            SMS Content
                          </label>
                          <div className="text-muted small">
                            <span className="me-3">
                              Words: <strong>{wordCount}</strong>
                            </span>
                            <span>
                              Characters: <strong>{characterCount}</strong>
                            </span>
                          </div>
                        </div>

                        <textarea
                          rows={6}
                          className="form-control border-2"
                          disabled={!editing}
                          placeholder="Type your SMS template..."
                          {...register("content")}
                        ></textarea>

                        {/* Placeholder Chips */}
                        <div className="mt-2 p-3 bg-light rounded border">
                          <small className="text-muted fw-semibold d-block mb-1">
                            Available placeholders:
                          </small>
                          <div className="d-flex flex-wrap gap-1">
                            {["PATIENT_NAME", "DATE", "TIME", "CL_ORDER_LINK", "SURVEY_LINK", "THANK_YOU_LINK"].map(
                              (p) => (
                                <code key={p} className="bg-white px-2 py-1 rounded border text-primary">
                                  {`{${p}}`}
                                </code>
                              )
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Save Buttons */}
                      {editing && (
                        <div className="border-top pt-3">
                          <div className="d-flex gap-2">
                            <button type="submit" className="btn btn-primary" disabled={loading}>
                              {loading ? (
                                <>
                                  <span className="spinner-border spinner-border-sm me-2"></span>
                                  Saving...
                                </>
                              ) : (
                                <>
                                  <i className="bi bi-check-lg me-1"></i>
                                  Save Template
                                </>
                              )}
                            </button>
                            <button
                              type="button"
                              className="btn btn-outline-secondary"
                              onClick={() => {
                                setEditing(false);
                                loadTemplateById(selectedId);
                              }}
                              disabled={loading}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* RIGHT COLUMN — iPhone Preview */}
                    <div className="col-md-5 ps-4">
                      <h6 className="fw-semibold mb-3">Live SMS Preview</h6>
                      <SmsPhonePreview message={contentValue} />
                    </div>

                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SmsTemplates;
